import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Download, X, ChevronDown, ChevronRight, CheckSquare, Square } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function fmt(n) {
  if (!n) return "—";
  return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Math.abs(n));
}

function keyOf(s) {
  return JSON.stringify(s);
}

/** One row with a checkbox + indent */
function CheckRow({ checked, onToggle, children, level = 0, testId }) {
  return (
    <button
      onClick={onToggle}
      data-testid={testId}
      className={`w-full flex items-center gap-2 text-left px-2 py-1.5 rounded hover:bg-slate-50 ${
        checked ? "bg-emerald-50/60" : ""
      }`}
      style={{ paddingLeft: 8 + level * 18 }}
    >
      {checked ? (
        <CheckSquare className="w-4 h-4 text-emerald-600 shrink-0" />
      ) : (
        <Square className="w-4 h-4 text-slate-400 shrink-0" />
      )}
      <div className="flex-1 flex items-center justify-between min-w-0 gap-2">
        {children}
      </div>
    </button>
  );
}

function CollapsibleSection({ label, defaultOpen = false, children, count }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-slate-100 py-2">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 text-sm font-semibold text-slate-800 py-1"
      >
        {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        <span>{label}</span>
        {count > 0 && (
          <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">
            {count} selected
          </span>
        )}
      </button>
      {open && <div className="mt-1">{children}</div>}
    </div>
  );
}

export default function MISSchedulePicker({ companyContext, filters, trigger }) {
  const [open, setOpen] = useState(false);
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [selected, setSelected] = useState([]); // array of schedule specs

  const companyId = companyContext?.companyId || "default";
  const branchId = companyContext?.branchId || null;

  useEffect(() => {
    if (!open || catalog) return;
    setLoading(true);
    const params = { ...filters, company_id: companyId };
    if (branchId) params.branch_id = branchId;
    axios
      .get(`${API}/mis/schedules/catalog`, { params })
      .then((res) => setCatalog(res.data))
      .catch(() => toast.error("Failed to load schedule catalog"))
      .finally(() => setLoading(false));
  }, [open, catalog, filters, companyId, branchId]);

  const isChecked = (spec) => selected.some((s) => keyOf(s) === keyOf(spec));
  const toggle = (spec) => {
    setSelected((prev) =>
      prev.some((s) => keyOf(s) === keyOf(spec))
        ? prev.filter((s) => keyOf(s) !== keyOf(spec))
        : [...prev, spec]
    );
  };

  const countInSection = (specs) => specs.filter(isChecked).length;

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const body = {
        from_date: filters.from_date || null,
        to_date: filters.to_date || null,
        company_id: companyId,
        branch_id: branchId,
        schedules: selected,
      };
      const res = await axios.post(`${API}/mis/export/custom`, body, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `MIS_Report_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Excel downloaded with ${selected.length} custom schedule${selected.length === 1 ? "" : "s"}`);
      setOpen(false);
    } catch (e) {
      toast.error("Export failed: " + e.message);
    } finally {
      setDownloading(false);
    }
  };

  const triggerEl = trigger || (
    <Button
      onClick={() => setOpen(true)}
      className="bg-zinc-950 text-white hover:bg-zinc-800 text-[12px] font-bold h-9 px-4 rounded-md"
      data-testid="export-excel-btn"
    >
      <Download className="w-3.5 h-3.5 mr-1.5" />
      Export Excel
    </Button>
  );

  return (
    <>
      <span onClick={() => setOpen(true)}>{triggerEl}</span>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          data-testid="schedule-picker-modal"
          onClick={(e) => e.target === e.currentTarget && setOpen(false)}
        >
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h2 className="text-lg font-bold text-slate-900">Customize MIS Excel</h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  Pick items to include as month-wise × party-wise schedules in the download
                </p>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="p-2 hover:bg-slate-100 rounded-md"
                data-testid="schedule-picker-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-6 py-3">
              {loading && (
                <div className="flex items-center justify-center py-12">
                  <div className="spinner" />
                </div>
              )}
              {!loading && catalog && (
                <>
                  {/* Party reports */}
                  <CollapsibleSection
                    label="Quick Party Reports"
                    defaultOpen={true}
                    count={countInSection(catalog.party_reports.map((p) => ({ kind: p.kind })))}
                  >
                    {catalog.party_reports.map((p) => {
                      const spec = { kind: p.kind };
                      return (
                        <CheckRow
                          key={p.kind}
                          checked={isChecked(spec)}
                          onToggle={() => toggle(spec)}
                          testId={`pick-${p.kind}`}
                        >
                          <span className="text-sm text-slate-700">{p.label}</span>
                        </CheckRow>
                      );
                    })}
                  </CollapsibleSection>

                  {/* Balance Sheet groups */}
                  <CollapsibleSection
                    label="Balance Sheet Groups (Party × Monthly)"
                    defaultOpen={true}
                    count={countInSection(
                      catalog.bs_groups.map((g) => ({ kind: "bs_group", bs_group: g.group }))
                    )}
                  >
                    {catalog.bs_groups.map((g) => {
                      const spec = { kind: "bs_group", bs_group: g.group };
                      return (
                        <CheckRow
                          key={`${g.side}-${g.group}`}
                          checked={isChecked(spec)}
                          onToggle={() => toggle(spec)}
                          testId={`pick-bs-${g.group.replace(/\s+/g, "-")}`}
                        >
                          <span className="text-sm text-slate-700 truncate">
                            <span className="text-[10px] text-slate-400 mr-2 uppercase tracking-wide">{g.side}</span>
                            {g.group}
                          </span>
                          <span className="text-xs font-mono tabular-nums text-slate-500 shrink-0">
                            ₹ {fmt(g.total)}
                          </span>
                        </CheckRow>
                      );
                    })}
                  </CollapsibleSection>

                  {/* P&L sections */}
                  {Object.entries(catalog.pl_sections).map(([head, subheads]) => {
                    if (!subheads.length) return null;
                    const headSpecs = subheads.map((s) => ({ kind: "mis_subhead", mis_subhead: s.subhead }));
                    const ledgerSpecs = subheads.flatMap((s) =>
                      s.ledgers.map((l) => ({
                        kind: "ledger",
                        mis_subhead: s.subhead,
                        ledger: l.name,
                      }))
                    );
                    const all = [...headSpecs, ...ledgerSpecs];
                    return (
                      <CollapsibleSection key={head} label={head} count={countInSection(all)}>
                        {subheads.map((s) => {
                          const sspec = { kind: "mis_subhead", mis_subhead: s.subhead };
                          return (
                            <div key={s.subhead} className="mb-1">
                              <CheckRow
                                checked={isChecked(sspec)}
                                onToggle={() => toggle(sspec)}
                                testId={`pick-sub-${s.subhead.replace(/\s+/g, "-")}`}
                              >
                                <span className="text-sm font-semibold text-slate-800">{s.subhead}</span>
                                <span className="text-xs font-mono tabular-nums text-slate-500 shrink-0">
                                  ₹ {fmt(s.total)}
                                </span>
                              </CheckRow>
                              {s.ledgers.map((l) => {
                                const lspec = { kind: "ledger", mis_subhead: s.subhead, ledger: l.name };
                                return (
                                  <CheckRow
                                    key={l.name}
                                    checked={isChecked(lspec)}
                                    onToggle={() => toggle(lspec)}
                                    level={1}
                                    testId={`pick-lgr-${l.name.replace(/\s+/g, "-")}`}
                                  >
                                    <span className="text-sm text-slate-600 truncate">{l.name}</span>
                                    <span className="text-[11px] font-mono tabular-nums text-slate-400 shrink-0">
                                      ₹ {fmt(l.total)}
                                    </span>
                                  </CheckRow>
                                );
                              })}
                            </div>
                          );
                        })}
                      </CollapsibleSection>
                    );
                  })}
                </>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 bg-slate-50">
              <div className="text-sm text-slate-600">
                <span className="font-bold text-slate-900">{selected.length}</span> schedule
                {selected.length === 1 ? "" : "s"} selected
                {selected.length > 0 && (
                  <button
                    onClick={() => setSelected([])}
                    className="ml-3 text-xs text-slate-500 hover:text-slate-900 underline"
                  >
                    clear all
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleDownload}
                  disabled={downloading}
                  className="bg-zinc-950 text-white hover:bg-zinc-800"
                  data-testid="schedule-picker-download"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {downloading
                    ? "Generating..."
                    : `Download (${3 + selected.length} tabs)`}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
